// Reimpresión de etiquetas de un pedido, disponible desde el estado CONTROLADO
// en adelante (encargado, admin y ventas).
//
// Dos usos:
//  1) La impresión física falla ~1 de cada 10 (glitch de PDFtoPrinter en la
//     PC-puente) y el pedido queda sin etiqueta. Este botón reencola el trabajo.
//  2) El encargado cargó mal la cantidad de bultos/paquetes: ANTES del despacho
//     puede corregirla acá y reimprimir; después del despacho solo reimprime las
//     mismas etiquetas (editar la cantidad ahí desincronizaría el conteo de la
//     agencia/camión).
//
// Modos:
//  - mostrarCantidades: muestra las cantidades en recuadros y separa las dos
//    acciones -> "Editar cantidades" (edita las cards en el lugar + Imprimir) y
//    "Reimprimir etiquetas" (reimprime las mismas, sin editar). Se usa en la
//    vista del encargado (controlado).
//  - sin mostrarCantidades: panel clásico desplegable (ventas / post-despacho).
//
// Además muestra en vivo el estado del último trabajo de impresión de este
// pedido (🖨️ Impresa / ⏳ En cola / ⚠️ Falló), para que el encargado VEA si salió
// en vez de adivinar.

import React, { useEffect, useMemo, useState } from "react";
import { db } from "../../../firebase";
import { doc, updateDoc, collection, query, where, onSnapshot } from "firebase/firestore";
import { useAuth } from "../../../context/AuthContext";
import { useApp } from "../../../context/AppContext";
import { encolarEtiquetas } from "../services/printQueue";
import { useEtiquetasOff } from "../hooks/useEtiquetasOff";

// Estado del último printJob del pedido, resumido para el cartelito.
function estadoImpresion(jobs) {
  if (!jobs || jobs.length === 0) return null;
  const ult = jobs.reduce((a, b) => {
    const ta = a?.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
    const tb = b?.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
    return tb > ta ? b : a;
  });
  return ult?.status || null; // pending | printing | done | error
}

export default function ReimprimirEtiquetas({ pedido, coleccion = "pedidos", puedeEditarCantidad = false, mostrarCantidades = false }) {
  const { user } = useAuth();
  const { toast, haptics } = useApp();
  const etiquetasOff = useEtiquetasOff();

  const [abierto, setAbierto]   = useState(false);
  const [editando, setEditando] = useState(false);
  const [bultos, setBultos]     = useState(String(pedido?.bultos ?? ""));
  const [paquetes, setPaquetes] = useState(String(pedido?.paquetes ?? ""));
  const [saving, setSaving]     = useState(false);
  const [jobs, setJobs]         = useState([]);

  // Pedidos "agregados" no tienen etiquetas propias (van con el paquete de otro).
  const esAgregado = pedido?.agregado === true;

  // Escuchar los trabajos de impresión de este pedido para el estado en vivo.
  useEffect(() => {
    if (!pedido?.id) return;
    const q = query(collection(db, "printJobs"), where("pedidoId", "==", pedido.id));
    const unsub = onSnapshot(q, (snap) => {
      setJobs(snap.docs.map(d => d.data()));
    }, () => {});
    return () => unsub();
  }, [pedido?.id]);

  const estado = useMemo(() => estadoImpresion(jobs), [jobs]);

  // Reimprime las MISMAS etiquetas (cantidad actual del pedido), sin editar.
  async function reimprimirAhora() {
    if (etiquetasOff) { toast.error("La impresión de etiquetas está desactivada."); return; }
    if (esAgregado) { toast.error("Pedido agregado: va con el paquete de otro, sin etiquetas propias."); return; }
    const nB = Number(pedido?.bultos) || 0;
    const nP = Number(pedido?.paquetes) || 0;
    if (nB < 1 && nP < 1) { toast.error("No hay bultos ni paquetes para imprimir."); return; }
    setSaving(true);
    try {
      await encolarEtiquetas({ ...pedido, id: pedido.id }, { bultos: nB, paquetes: nP, uid: user?.uid });
      haptics?.success?.();
      toast.success(`Etiquetas reenviadas a la impresora (${nB} bultos + ${nP} paquetes)`);
    } catch (e) {
      console.error("[ReimprimirEtiquetas] reimprimirAhora", e);
      haptics?.error?.();
      toast.error("No se pudieron reenviar las etiquetas.");
    } finally {
      setSaving(false);
    }
  }

  // Guarda la corrección de cantidad (solo pre-despacho) y reimprime.
  async function confirmarEdicion() {
    if (etiquetasOff) { toast.error("La impresión de etiquetas está desactivada."); return; }
    if (esAgregado) { toast.error("Pedido agregado: va con el paquete de otro, sin etiquetas propias."); return; }
    const nB = Number(bultos) || 0;
    const nP = Number(paquetes) || 0;
    if (nB < 1 && nP < 1) { toast.error("Cargá al menos 1 bulto o paquete."); return; }
    setSaving(true);
    try {
      await updateDoc(doc(db, coleccion, pedido.id), { bultos: nB, paquetes: nP });
      await encolarEtiquetas({ ...pedido, id: pedido.id }, { bultos: nB, paquetes: nP, uid: user?.uid });
      haptics?.success?.();
      toast.success(`Cantidades actualizadas y etiquetas reenviadas (${nB} bultos + ${nP} paquetes)`);
      setEditando(false);
    } catch (e) {
      console.error("[ReimprimirEtiquetas] confirmarEdicion", e);
      haptics?.error?.();
      toast.error("No se pudo guardar/reimprimir.");
    } finally {
      setSaving(false);
    }
  }

  // Panel clásico (ventas / post-despacho): edita si puede y reimprime.
  async function reimprimir() {
    if (etiquetasOff) { toast.error("La impresión de etiquetas está desactivada."); return; }
    if (esAgregado) { toast.error("Pedido agregado: va con el paquete de otro, sin etiquetas propias."); return; }

    setSaving(true);
    try {
      let nB = Number(pedido?.bultos) || 0;
      let nP = Number(pedido?.paquetes) || 0;

      if (puedeEditarCantidad) {
        nB = Number(bultos) || 0;
        nP = Number(paquetes) || 0;
        if (nB < 1 && nP < 1) { toast.error("Cargá al menos 1 bulto o paquete."); setSaving(false); return; }
        await updateDoc(doc(db, coleccion, pedido.id), { bultos: nB, paquetes: nP });
      }

      await encolarEtiquetas({ ...pedido, id: pedido.id }, { bultos: nB, paquetes: nP, uid: user?.uid });
      haptics?.success?.();
      toast.success(`Etiquetas reenviadas a la impresora (${nB} bultos + ${nP} paquetes)`);
      setAbierto(false);
    } catch (e) {
      console.error("[ReimprimirEtiquetas] error", e);
      haptics?.error?.();
      toast.error("No se pudieron reenviar las etiquetas.");
    } finally {
      setSaving(false);
    }
  }

  if (etiquetasOff || esAgregado) return null;

  const badge = estado && {
    done:     { txt: "🖨️ Impresa",  bg: "#f0fdf4", bd: "#86efac", fg: "#166534" },
    printing: { txt: "⏳ Imprimiendo…", bg: "#eff6ff", bd: "#bfdbfe", fg: "#1d4ed8" },
    pending:  { txt: "⏳ En cola",   bg: "#eff6ff", bd: "#bfdbfe", fg: "#1d4ed8" },
    error:    { txt: "⚠️ Falló — reimprimí", bg: "#fef2f2", bd: "#fca5a5", fg: "#b91c1c" },
  }[estado];

  // Estilos de las cards de cantidad.
  const cardStyle = { background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "10px", padding: "10px", textAlign: "center" };
  const numStyle  = { margin: 0, fontSize: "1.5rem", fontWeight: 800, color: "#0f172a" };
  const lblStyle  = { margin: "2px 0 0", fontSize: "0.66rem", color: "#94a3b8", fontWeight: 700, textTransform: "uppercase" };
  const inpStyle  = { width: "100%", textAlign: "center", fontSize: "1.3rem", fontWeight: 800, color: "#0f172a", border: "1.5px solid #f59e0b", borderRadius: "8px", padding: "4px", boxSizing: "border-box", fontFamily: "inherit" };

  return (
    <div style={{ background: "#fff", border: "1.5px solid #e2e8f0", borderRadius: "14px", padding: "12px 14px", marginBottom: "14px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
        <span style={{ fontSize: "0.72rem", fontWeight: 700, color: "#64748b", letterSpacing: "0.06em", textTransform: "uppercase" }}>
          Etiquetas
        </span>
        {badge && (
          <span style={{ fontSize: "0.74rem", fontWeight: 700, color: badge.fg, background: badge.bg, border: `1px solid ${badge.bd}`, borderRadius: "999px", padding: "2px 10px" }}>
            {badge.txt}
          </span>
        )}
        {!mostrarCantidades && !abierto && (
          <button
            type="button"
            onClick={() => setAbierto(true)}
            style={{ marginLeft: "auto", padding: "8px 14px", borderRadius: "10px", border: "1.5px solid #cbd5e1", background: "#f8fafc", color: "#0f172a", fontWeight: 700, fontSize: "0.85rem", cursor: "pointer" }}
          >
            {puedeEditarCantidad ? "✏️ Editar / Reimprimir" : "🔁 Reimprimir"}
          </button>
        )}
      </div>

      {/* Modo con cantidades visibles (encargado controlado) */}
      {mostrarCantidades && (
        <div style={{ marginTop: "12px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
            <div style={cardStyle}>
              {editando
                ? <input autoFocus type="number" inputMode="numeric" min="0" value={bultos} onChange={e => setBultos(e.target.value)} style={inpStyle} />
                : <p style={numStyle}>{pedido?.bultos ?? "—"}</p>}
              <p style={lblStyle}>Bultos</p>
            </div>
            <div style={cardStyle}>
              {editando
                ? <input type="number" inputMode="numeric" min="0" value={paquetes} onChange={e => setPaquetes(e.target.value)} style={inpStyle} />
                : <p style={numStyle}>{pedido?.paquetes ?? "—"}</p>}
              <p style={lblStyle}>Paquetes</p>
            </div>
          </div>

          {editando ? (
            <div style={{ display: "flex", gap: "8px", marginTop: "10px" }}>
              <button
                type="button"
                disabled={saving}
                onClick={() => { setBultos(String(pedido?.bultos ?? "")); setPaquetes(String(pedido?.paquetes ?? "")); setEditando(false); }}
                style={{ flex: "0 0 auto", padding: "10px 14px", borderRadius: "10px", border: "1.5px solid #cbd5e1", background: "#fff", color: "#64748b", fontWeight: 700, fontSize: "0.85rem", cursor: "pointer" }}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={confirmarEdicion}
                disabled={saving}
                style={{ flex: 1, padding: "10px 14px", borderRadius: "10px", border: "none", background: saving ? "#e2e8f0" : "#0f172a", color: saving ? "#94a3b8" : "#fff", fontWeight: 700, fontSize: "0.9rem", cursor: saving ? "not-allowed" : "pointer" }}
              >
                {saving ? "Guardando…" : "🖨️ Imprimir"}
              </button>
            </div>
          ) : (
            <div style={{ display: "flex", gap: "8px", marginTop: "10px" }}>
              {puedeEditarCantidad && (
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => { setBultos(String(pedido?.bultos ?? "")); setPaquetes(String(pedido?.paquetes ?? "")); setEditando(true); }}
                  style={{ flex: 1, padding: "10px 14px", borderRadius: "10px", border: "1.5px solid #cbd5e1", background: "#f8fafc", color: "#0f172a", fontWeight: 700, fontSize: "0.85rem", cursor: "pointer" }}
                >
                  ✏️ Editar cantidades
                </button>
              )}
              <button
                type="button"
                onClick={reimprimirAhora}
                disabled={saving}
                style={{ flex: 1, padding: "10px 14px", borderRadius: "10px", border: "none", background: saving ? "#e2e8f0" : "#0f172a", color: saving ? "#94a3b8" : "#fff", fontWeight: 700, fontSize: "0.85rem", cursor: saving ? "not-allowed" : "pointer" }}
              >
                🔁 Reimprimir etiquetas
              </button>
            </div>
          )}
        </div>
      )}

      {/* Panel clásico desplegable (ventas / post-despacho) */}
      {!mostrarCantidades && abierto && (
        <div style={{ marginTop: "12px" }}>
          {puedeEditarCantidad ? (
            <>
              <p style={{ margin: "0 0 8px", fontSize: "0.78rem", color: "#64748b" }}>
                Corregí la cantidad si hace falta y reimprimí:
              </p>
              <div style={{ display: "flex", gap: "10px", marginBottom: "10px" }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: "block", fontSize: "0.72rem", fontWeight: 700, color: "#64748b", marginBottom: "4px" }}>Bultos</label>
                  <input
                    type="number" inputMode="numeric" min="0" value={bultos}
                    onChange={e => setBultos(e.target.value)}
                    style={{ width: "100%", padding: "10px", borderRadius: "10px", border: "1.5px solid #cbd5e1", fontSize: "1rem", boxSizing: "border-box" }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: "block", fontSize: "0.72rem", fontWeight: 700, color: "#64748b", marginBottom: "4px" }}>Paquetes</label>
                  <input
                    type="number" inputMode="numeric" min="0" value={paquetes}
                    onChange={e => setPaquetes(e.target.value)}
                    style={{ width: "100%", padding: "10px", borderRadius: "10px", border: "1.5px solid #cbd5e1", fontSize: "1rem", boxSizing: "border-box" }}
                  />
                </div>
              </div>
            </>
          ) : (
            <p style={{ margin: "0 0 10px", fontSize: "0.78rem", color: "#64748b" }}>
              Se reimprimen las mismas etiquetas ({Number(pedido?.bultos) || 0} bultos + {Number(pedido?.paquetes) || 0} paquetes).
              {" "}La cantidad ya no se puede cambiar (pedido despachado).
            </p>
          )}
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              type="button"
              onClick={() => setAbierto(false)}
              disabled={saving}
              style={{ flex: "0 0 auto", padding: "10px 14px", borderRadius: "10px", border: "1.5px solid #cbd5e1", background: "#fff", color: "#64748b", fontWeight: 700, fontSize: "0.85rem", cursor: "pointer" }}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={reimprimir}
              disabled={saving}
              style={{ flex: 1, padding: "10px 14px", borderRadius: "10px", border: "none", background: saving ? "#e2e8f0" : "#0f172a", color: saving ? "#94a3b8" : "#fff", fontWeight: 700, fontSize: "0.9rem", cursor: saving ? "not-allowed" : "pointer" }}
            >
              {saving ? "Enviando…" : "🖨️ Reimprimir etiquetas"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
